import {
  Checkbox,
  FormHelperText,
  TextField,
  Box,
  Stack,
  Typography,
  useTheme,
  FormControlLabel,
} from "@mui/material";
import React, { useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { FormattedMessage, useIntl } from "react-intl";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  GET_DEGREE_PROGRAMS,
  GET_UNIVERSITIES,
} from "../../../apollo-client/queries";
import { Link } from "react-router-dom";
import { showErrorToast } from "../../../toast/toast-service";

export const SSORegisterFields = ({
  formValues,
  setFormValues,
  formValueErrors,
  setFormValueErrors,
  refs,
}) => {
  const [universityOptions, setUniversityOptions] = useState([]);
  const [degreeProgramOptions, setDegreeProgramOptions] = useState([]);
  const theme = useTheme();

  const intl = useIntl();

  const { universityRef, degreeProgramRef } = refs;

  const { data: universitiesData, loading: loadingUniversites } = useQuery(
    GET_UNIVERSITIES,
    {
      variables: { limit: 999 },
      onCompleted: (data) => {
        setUniversityOptions(data.universities);
      },
      onError: (error) => {
        showErrorToast(error.message);
      },
      fetchPolicy: "network-only",
    }
  );

  const [
    getDegreePrograms,
    { data: degreeProgramsData, loading: loadingDegreePrograms },
  ] = useLazyQuery(GET_DEGREE_PROGRAMS, {
    variables: {
      university: formValues.university,
      limit: 999,
    },
    onCompleted: (data) => {
      setDegreeProgramOptions(data.degreePrograms);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
    fetchPolicy: "cache-and-network",
  });

  const resetFormErrors = () => {
    setFormValueErrors({
      university: false,
      degreeProgram: false,
      areAGBsChecked: false,
    });
  };

  const validateField = (fieldName, value) => {
    if (value === "" || (fieldName === "areAGBsChecked" && !value)) {
      const errorMessage = intl.formatMessage({
        id: fieldName === "areAGBsChecked" 
          ? "registration.AGBs-checked.error" 
          : "login-errors-general-required"
      });
      setFormValueErrors(prev => ({
        ...prev,
        [fieldName]: errorMessage
      }));
      return false;
    } else {
      setFormValueErrors(prev => ({
        ...prev,
        [fieldName]: false
      }));
      return true;
    }
  };

  const handleUniversityInputChange = async (event, value) => {
    const universityInput = value && value.name;

    setFormValues({
      ...formValues,
      university: universityInput,
    });
    
    if (universityInput) {
      validateField("university", universityInput);
    } else {
      validateField("university", "");
    }
    
    await getDegreePrograms();
  };

  const handleDegreeProgramInputChange = async (event, value) => {
    const selectedDegreeProgram = value && value.name;

    setFormValues({
      ...formValues,
      degreeProgram: selectedDegreeProgram,
    });

    if (selectedDegreeProgram) {
      validateField("degreeProgram", selectedDegreeProgram);
    } else {
      validateField("degreeProgram", "");
    }
  };

  const handleAGBCheckboxChange = (event) => {
    const isCheckboxChecked = event.target.checked;

    setFormValues({
      ...formValues,
      areAGBsChecked: isCheckboxChecked,
    });
    
    validateField("areAGBsChecked", isCheckboxChecked);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Box sx={{ width: '100%', maxWidth: '320px' }}>
        <Stack spacing={3}>
          <Autocomplete
            id="unis-autocomplete"
            options={universityOptions}
            loading={loadingUniversites}
            onChange={handleUniversityInputChange}
            size="medium"
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({ id: "general.university" })}
                inputRef={universityRef}
                variant="outlined"
                error={Boolean(formValueErrors.university)}
                helperText={formValueErrors.university}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '15px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '& fieldset': {
                      border: '2px solid rgba(232, 121, 117, 0.1)',
                      borderRadius: '12px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(232, 121, 117, 0.3)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#E87975',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      boxShadow: '0 0 0 3px rgba(232, 121, 117, 0.1)',
                    },
                    '&.Mui-error fieldset': {
                      borderColor: '#f44336',
                      borderWidth: '2px',
                    },
                    '&.Mui-error': {
                      backgroundColor: 'rgba(244, 67, 54, 0.05)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.grey[600],
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#E87975',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '12px',
                    fontWeight: 500,
                  },
                }}
              />
            )}
          />

          <Autocomplete
            id="degree-program-autocomplete"
            options={degreeProgramOptions}
            loading={loadingDegreePrograms}
            onChange={handleDegreeProgramInputChange}
            size="medium"
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({
                  id: "registration.your-degree-program",
                })}
                variant="outlined"
                inputRef={degreeProgramRef}
                error={Boolean(formValueErrors.degreeProgram)}
                helperText={formValueErrors.degreeProgram}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '15px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '& fieldset': {
                      border: '2px solid rgba(232, 121, 117, 0.1)',
                      borderRadius: '12px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(232, 121, 117, 0.3)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#E87975',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      boxShadow: '0 0 0 3px rgba(232, 121, 117, 0.1)',
                    },
                    '&.Mui-error fieldset': {
                      borderColor: '#f44336',
                      borderWidth: '2px',
                    },
                    '&.Mui-error': {
                      backgroundColor: 'rgba(244, 67, 54, 0.05)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.grey[600],
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#E87975',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '12px',
                    fontWeight: 500,
                  },
                }}
              />
            )}
          />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
            <Checkbox
              checked={Boolean(formValues.areAGBsChecked)}
              onChange={handleAGBCheckboxChange}
              sx={{
                color: '#E87975',
                '&.Mui-checked': {
                  color: '#E87975',
                },
                '&:hover': {
                  backgroundColor: 'rgba(232, 121, 117, 0.1)',
                },
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  color: theme.palette.grey[700],
                  lineHeight: 1.4,
                }}
              >
                <FormattedMessage id={"registration.AGBs-checked.1"} />
                <Link 
                  to="/AGBs" 
                  style={{ 
                    color: '#E87975', 
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  <FormattedMessage id={"registration.AGBs-checked.2"} />
                </Link>
                <FormattedMessage id={"registration.AGBs-checked.3"} />
                <Link 
                  to="/AGBs" 
                  style={{ 
                    color: '#E87975', 
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  <FormattedMessage id={"registration.AGBs-checked.4"} />
                </Link>
                <FormattedMessage id={"registration.AGBs-checked.5"} />
              </Typography>
              {formValueErrors.areAGBsChecked && (
                <FormHelperText sx={{ color: '#f44336', fontSize: '12px', mt: 0.5 }}>
                  <FormattedMessage id="registration.AGBs-checked.error" />
                </FormHelperText>
              )}
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

